// ─────────────────────────────────────────────────────────────────────
// Acero / Piedra Variant Handler
// Rewritten for chip-based variant picker (replaces dropdown version).
// ─────────────────────────────────────────────────────────────────────
//
// What it does:
//   1. Updates chip selected state (visual + aria) on click
//   2. Finds the variant whose options match the new selection
//   3. Updates the URL ?variant=ID so Shopify recognises the change
//   4. Fires a `change` event on a real <select name="options[...]"> if one
//      exists, so theme components (price, availability, gallery, cart) react
//   5. Scrolls the gallery to a matching image (alt-text based, unchanged)
//
// The legacy dropdown helpers (togglePiedraMenu, .piedra-custom-dropdown
// queries) are kept as no-ops so any stray inline onclick from older
// templates won't throw.
// ─────────────────────────────────────────────────────────────────────

console.log('✅ Acero variant handler loaded (chip version)');

// ─── Legacy no-op (kept so old onclick handlers don't crash) ────────
function togglePiedraMenu(/* el */) {
  // Dropdown removed. Intentionally empty.
}

// ─── Main entry point: called from each chip's onclick ──────────────
function updatePiedraVariant(el, optionHandle, value) {
  console.log('🎯 updatePiedraVariant:', { optionHandle, value });

  // 1. Update chip visual state within this option group
  const group = el.closest('.acero-variant-picker__group');
  if (group) {
    group.querySelectorAll('.acero-chip').forEach((chip) => {
      const isMe = chip === el;
      chip.classList.toggle('acero-chip--selected', isMe);
      chip.setAttribute('aria-checked', isMe ? 'true' : 'false');
    });
  }

  // 2. Determine the FULL set of currently selected option values across all groups.
  //    For single-option products this is just [value], but multi-option products
  //    (Material + Dimensions, etc.) need the full combo to find the right variant.
  const picker = el.closest('.acero-variant-picker');
  const selectedValues = [];
  if (picker) {
    picker.querySelectorAll('.acero-variant-picker__group').forEach((g) => {
      const sel = g.querySelector('.acero-chip--selected');
      if (sel) {
        selectedValues.push(sel.getAttribute('data-value'));
      }
    });
  }
  console.log('  selected combo:', selectedValues);

  // 3. Find the variant matching this combo (variant data is exposed in product JSON)
  const variant = findVariantByOptions(selectedValues);
  console.log('  matched variant:', variant ? variant.id : 'none');

  if (variant) {
    // 4. Update URL so Shopify and any deep-link sharing reflect the choice
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());

    // 5. Update the cart form's hidden variant ID input. This is what actually
    //    gets submitted when the user clicks Add to Cart, regardless of theme components.
    syncCartFormVariantId(variant.id);

    // 6. Try to notify the theme's variant system if a real <select> exists.
    //    Feather may render a hidden <select name="options[Material]"> for
    //    form submission; if so, change it and dispatch event.
    syncThemeVariantSelectors(optionHandle, value);
  }

  // 7. Scroll gallery to matching image (existing behavior, unchanged)
  setTimeout(() => scrollToImageByValue(value), 400);
}

// ─── Update the cart form's hidden variant ID input ─────────────────
// This is the most important sync: it determines which variant actually gets
// added to cart when "Add to Cart" is clicked, regardless of theme components.
function syncCartFormVariantId(variantId) {
  // Shopify cart forms use <input name="id" value="VARIANT_ID">
  const idInputs = document.querySelectorAll('form[action*="/cart/add"] input[name="id"]');
  idInputs.forEach((input) => {
    input.value = variantId;
    console.log('  synced cart form variant id to ' + variantId);
  });

  // Some themes use <product-form> custom elements with a data-variant-id attr
  const productForms = document.querySelectorAll('product-form, [data-variant-id]');
  productForms.forEach((el) => {
    if (el.hasAttribute('data-variant-id')) {
      el.setAttribute('data-variant-id', variantId);
    }
  });

  // Feather's sticky-add-to-cart component: update its data attribute
  const stickyCart = document.querySelector('sticky-add-to-cart');
  if (stickyCart) {
    stickyCart.setAttribute('data-current-variant-id', variantId);
  }
}

// ─── Find variant by exact match of selected option values ──────────
function findVariantByOptions(selectedValues) {
  // Shopify exposes product JSON via [type="application/json"][data-product-json]
  // or via a global. The structured-data <script> in product-information.liquid
  // also has it but in a different shape. Try a few sources.
  const variants = readProductVariants();
  if (!variants || !variants.length) return null;

  return variants.find((v) => {
    // v.options is an array like ["Stainless steel", "6-bottle"]
    if (!v.options || v.options.length !== selectedValues.length) return false;
    return v.options.every((opt, i) => opt === selectedValues[i]);
  });
}

// ─── Read product variant data from whatever's available on the page ──
function readProductVariants() {
  // Strategy 1 (canonical): JSON tag injected by our variant picker block
  const aceroData = document.querySelector('.acero-variant-data');
  if (aceroData) {
    try {
      const data = JSON.parse(aceroData.textContent);
      if (data.variants) return data.variants;
    } catch (e) {
      console.warn('Could not parse acero-variant-data', e);
    }
  }

  // Strategy 2: Shopify's standard product JSON script tag (older themes)
  const productJson = document.querySelector('[data-product-json]');
  if (productJson) {
    try {
      const data = JSON.parse(productJson.textContent);
      if (data.variants) return data.variants;
    } catch (e) {
      console.warn('Could not parse data-product-json', e);
    }
  }

  // Strategy 3: global Shopify product object (some themes set this)
  if (window.Shopify && window.Shopify.product && window.Shopify.product.variants) {
    return window.Shopify.product.variants;
  }

  console.warn('No product variant data source found on page');
  return null;
}

// ─── Sync theme's hidden form selectors if any exist ────────────────
function syncThemeVariantSelectors(optionHandle, value) {
  // Look for a <select name="options[Material]"> the theme might use for cart submission.
  // Match on case-insensitive option name.
  const selects = document.querySelectorAll('select[name^="options["]');
  selects.forEach((sel) => {
    const nameAttr = sel.getAttribute('name') || '';
    const handleFromName = nameAttr
      .replace('options[', '')
      .replace(']', '')
      .toLowerCase()
      .replace(/\s+/g, '-');
    if (handleFromName === optionHandle) {
      sel.value = value;
      sel.dispatchEvent(new Event('change', { bubbles: true }));
      console.log('  synced <select> name=' + nameAttr + ' to ' + value);
    }
  });

  // Also look for radios in case the theme uses <input type="radio">
  const radios = document.querySelectorAll('input[type="radio"][name^="options["]');
  radios.forEach((radio) => {
    const nameAttr = radio.getAttribute('name') || '';
    const handleFromName = nameAttr
      .replace('options[', '')
      .replace(']', '')
      .toLowerCase()
      .replace(/\s+/g, '-');
    if (handleFromName === optionHandle && radio.value === value) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      console.log('  synced radio name=' + nameAttr + ' to ' + value);
    }
  });
}

// ─── Scroll gallery to image whose alt text matches the chosen value ─
function scrollToImageByValue(searchValue) {
  const allImages = document.querySelectorAll('img[alt]');
  const searchLower = searchValue.toLowerCase();

  let match = null;
  for (const img of allImages) {
    const altLower = (img.alt || '').toLowerCase();
    if (altLower === searchLower || altLower.includes(searchLower)) {
      const rect = img.getBoundingClientRect();
      if (rect.width > 100 && rect.height > 100) {
        match = img;
        break;
      }
    }
  }

  if (!match) return;

  const target =
    match.closest('.product-media') ||
    match.closest('[class*="gallery"]') ||
    match.closest('[class*="media"]') ||
    match;

  const offsetPosition = target.getBoundingClientRect().top + window.scrollY - 0.5;
  window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
}

// ─── Expose to global scope (inline onclick attributes need this) ────
window.togglePiedraMenu = togglePiedraMenu;
window.updatePiedraVariant = updatePiedraVariant;
